import React, { useState, useEffect, useCallback, useRef } from "react";
import { FLAVOR, icons, images } from "../../helpers/constants";
import TimeSelection from "../Buttons/TimeSelection";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import InfoPill from "../Pills/InfoPill";
import DeviceInfoCardWIP from "./DeviceInfoCardWIP";
import { externalPowerText, fixDate, fixDateEvent, fixDateEventNew, getBatteryIcon, getEventIcon } from "../../helpers/helperFunctions";
import PillButton from "../Buttons/PillButton";
import toast from "react-hot-toast";
import { useMapToggles } from "../Map/ToggleContext";
import { useNavigate } from "react-router-dom";
import * as request from "../../services/FetchService";
import AlarmCentralModal from '../Modals/AlarmCentralModal';
import ExtendedInfoModal from "../Modals/ExtendedInfoModal";
import ZoneModal from "../Modals/ZoneModal";
import { useIntervalWhenVisible, useIsMobile } from "../../helpers/customHooks";
import { AnimatePresence, motion } from "framer-motion";
import { useSidebarContext } from "../Sidebar/SiderbarContext";
import NewButton from "../Buttons/NewButton";
import { CustomSwitch } from "../Buttons/CustomSwitch";
import HistoryGraphNew from "../History/HistoryGraphNew";
import InfoSquare from "../History/InfoSquare";
import AlcolockModal from "../Modals/AlcolockModal";
import EventRowNew from "../Events/EventRowNew";

const dateCache = new Map(); // deviceId -> { startISO, endISO, preset }

const SelectedDevice = ({ deviceId, refresh, closeDevice, onReady, onCloseStart }) => {
	const { t } = useTranslation();
	const [extendedData, setExtendedData] = useState(null);
	const [extendedType, setExtendedType] = useState(null);
	const [loading, setLoading] = useState(true);
	const [device, setDevice] = useState(null);
	const { setActiveDevice, activeEvent, setActiveEvent, historyData, setHistoryData } = useMapToggles();
	const { actionbarContent, setTopbarContent } = useSidebarContext();
	const [currentEvent, setCurrentEvent] = useState(null);
	const navigate = useNavigate();

	const [events, setEvents] = useState([]);
	const MOBILE = useIsMobile();
	const prevHistoryDataRef = useRef();
	const [alcoType, setAlcoType] = useState(null)
	const [minDate, setMinDate] = useState(undefined)
	const [wrongDate, setWrongDate] = useState(false)
	const lang = localStorage.getItem("language") ?? "en"


	useEffect(() => {
		return () => {
			if(!MOBILE){
				setHistoryData(null)
			}
			if (deviceId) dateCache.delete(deviceId);
		};
	}, []);

	useEffect(() => {
		const prev = prevHistoryDataRef.current;

		if (prev && !historyData && !MOBILE) {
			const content = {
				left: (
						<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={handleClose}>
							<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
						</div>	
					),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t("devices.devices")}
					</motion.h4>
				)
			}
			setTopbarContent(content)
		}
		prevHistoryDataRef.current = historyData;
	}, [historyData]);

	useEffect(() => {
		if (deviceId) {
			getInfo();
			getEvents();
			const content = {
				left: (
						<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={handleClose}>
							<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
						</div>	
					),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t("devices.devices")}
					</motion.h4>
				)
			}
			setTopbarContent(content)
		} else {
		//	setTopbarContent(null)
		}
		return () => {
		//	setTopbarContent(null)
		}
	}, [deviceId]);

	const getInfo = async () => {
		try {
			const object = { deviceid: deviceId };
			const { data } = await request.post("", "device/info", object);

			if (data?.errorCode === "10003" || data?.errorCode === "10002") {
				localStorage.clear();
				window.location.href = "login";
				return;
			}
			// // 2️⃣  Make sure we always store a *real array* (fallback to [])
			// const list = Array.isArray(data?.devices) ? data.devices : [];
			// const match = list.find(device => device.id === deviceId)
			console.log(data?.device?.history_limit)
			const from = data?.device?.history_limit?.from
			if(from){
				setMinDate(dayjs(data?.device?.history_limit?.from).startOf("day"))
			}else{
				setMinDate(undefined)
			}
			setDevice(data?.device);
		} finally {
			setLoading(false);
			onReady?.(true);
		}
	};

	const getEvents = async () => {
		const object = {
			limit: 2,
			deviceid: [deviceId],
			type: process.env.REACT_APP_FLAVOR === "alco" ? ["highprio"] : null
		};
		const { data } = await request.post("", "events/list", object);
		setEvents(data?.events);
	};

	const updateData = useCallback(async () => {
		if (!deviceId) return;
		try {
			await getInfo();
			await getEvents();
		} finally {
		}
	}, [deviceId]);

	useIntervalWhenVisible(updateData, 10_000, deviceId);

	const [dates, setDates] = useState(() => {
		const cached = dateCache.get(deviceId);
		if (cached) {
			return {
				startDate: dayjs(cached.startISO),
				endDate: dayjs(cached.endISO),
				preset: cached.preset ?? null,
			};
		}
		return {
			startDate: dayjs().subtract(1, "day"),
			endDate: dayjs(),
			preset: 1,
		};
	});

	useEffect(() => {
		if (!deviceId) return;
		dateCache.set(deviceId, {
			startISO: dates.startDate?.toISOString(),
			endISO: dates.endDate?.toISOString(),
			preset: dates.preset ?? null,
		});
	}, [deviceId, dates]);

		const handleClose = () => {
			if (deviceId) dateCache.delete(deviceId);
			onCloseStart?.();             // 👈 tell parent: animate list back now
			closeDevice();        // calls parent after exit finished
		};



	const [modals, setModals] = useState({
		model: false,
		battery: false,
		override: false,
		alarmcentral: false,
		temp: false,
	});

	const toggleModal = (modalName) => {
		setExtendedData(null);
		setExtendedType(null);
		setModals((prevState) => ({
			...prevState,
			[modalName]: !prevState[modalName],
		}));
	};

	useEffect(() => {
		if (modals.battery) {
			fetchData("battery");
		}
		if (modals.temp) {
			fetchData("temp");
		}
		if (modals.humidity) {
			fetchData("humidity");
		}
		if (modals.stolenmode) {
			setExtendedData({ device: device, url: stolenmode.url });
			setExtendedType("stolenmode");
		}
		if (modals.dataplan) {
			setExtendedData({ device: device, url: stolenmode.url });
			setExtendedType("dataplan");
		}
		if(modals.override){
			setAlcoType("override")
		}
		if(modals.engineblock){
			setAlcoType("engineblock")
		}
	}, [modals]);

	const fetchData = (type) => {
		const data = {
			deviceid: deviceId,
			type: type,
		};
		setExtendedType(type);
		request.post("", "device/info/extended/statistics", data).then((response) => {
			if (response?.data?.successful) {
				const result = response?.data?.response;
				setExtendedData(result);
			} else {
				toggleModal(type);
				setExtendedType(null);
				toast(t("toast.model_no_support"), { icon: <img className="size_20 grey-blue-icon" src={icons.info_icon} alt="" />, duration: 2000 });
			}
		});
	};

	const toggleAlarm = (value, showModal = true) => {
		const data = {
			deviceid: device?.id,
			alarmcenter_arm: value,
		};
		request.post("", "device/update", data).then((response) => {
			if (response.data.successful) {
				refresh();
				getInfo()
				if(showModal){
					toggleModal("alarmcentral");
				}
				toast.success(t("toast.success"));
			} else {
				const msg = response?.data?.message
                toast.error(msg ?? t("toast.error"))
			}
		});
	};

	const toggleRelay = (mode) => {
		const data = {
			deviceid: device?.id,
			relayswitch: mode,
		};
		request.post("", "device/update", data).then((response) => {
			if (response.data.successful) {
				getInfo()
				refresh();
				//  toggleModal("alarmcentral")
				toast.success(t("toast.success"));
			} else {
				const msg = response?.data?.message
                toast.error(msg ?? t("toast.error"))
			}
		});
	};

	const showOnMap = (event, device) => {
		event.stopPropagation();
		setActiveEvent(null);
		if (positionExists) {
			setActiveDevice(device.id);
		} else {
			toast.error(t("toast.no_position_device_show"));
		}
	};

	const goToEvents = () => {
		navigate("/events", {state: {deviceid: device?.id}})
	};

	const getHistory = async () => {
		toast.loading(t("devices.fetching_history"), { id: "opening_history" });
		const data = {
			deviceid: device?.id,
			starttime: dates.startDate.toDate(),
			stoptime: dates.endDate.toDate(),
		};

		try {
			const response = await request.post("", "history/list/map", data);
			if (response.status === 200 && !response.data.errorCode) {
				const history = response?.data?.response;
				const historyDevice = history?.device;
				const positions = history?.positions;
				const stats = history?.stats;
				setHistoryData({ positions, stats, device: historyDevice });

				if (MOBILE) {
					navigate("/map");
				}
				return;
			}

			toast.error(response?.data?.api_message ?? t("toast.error"));
		} catch (error) {
			toast.error(t("toast.error"));
		} finally {
			toast.dismiss("opening_history");
		}
	};
	// const getHistoryTEST = () => {
	// 	toast.loading(t("devices.fetching_history"), { id: "opening_history" });
	// 	const data = {
	// 		deviceid: device?.id,
	// 		starttime: dates.startDate.toDate(),
	// 		stoptime: dates.endDate.toDate(),
	// 	};
	// 	request.post("", "history/list/polyline_unix", data).then((response) => {
	// 		console.log("RESPONSE : ", response)
	// 		if (response.status === 200 && !response.data.errorCode) {

	// 			const history = response?.data[0]
	// 			const device = history?.devicename
	// 			const pointsInfo = history?.pointsInfo
	// 			const positions = history?.positions
	// 			const stats = history?.stats
	// 			console.log("HISTORY?:", history)
	// 			setHistoryData({polyline: history?.polyline, device: device, pointsInfo: pointsInfo })
	// 			toast.dismiss("opening_history");
	// 			if(MOBILE){
	// 				navigate("/map")
	// 				return;
	// 			}
	// 		} else {
	// 			toast.dismiss("opening_history");
	// 			toast.error(response.data.api_message);
	// 		}
	// 	});
	// };


    const turnOffStolenMode = () => {
        const data = {
            deviceid: device?.id,
            action: "deactivate"
        }
        request.post("", "devices/stolenmode", data).then((response) => {
            if(response?.data?.successful){
                getInfo();
				refresh();
                toast.success(t("toast.stolen_mode_deactivated"))
				toggleModal(extendedType)
            }else{
                const msg = response?.data?.message
                toast.error(msg ?? t("toast.error"))
            }
        })
    }

	const updateAlcoInfo = (content) => {
		content.deviceid = [device?.id]
		request.post("", "devices/external/command", content).then((response) => {
			if (response.data.successful) {
				toast.success(t("toast.success"));
				getInfo();
				refresh();
				toggleModal(alcoType)
			} else {
				const msg = response?.data?.message
                toast.error(msg ?? t("toast.error"))
			}
		});		
	}

	const iotinfo = device?.iotinfo;
	const external_info = device?.external_hardwareinfo
	const vehicleinfo = device?.vehicleinfo;
	const positioninfo = iotinfo?.positioninfo;
	const externalbattery = iotinfo?.power?.powersupply?.info;
	const internalbattery_raw = iotinfo?.power?.battery?.info;
	const internalbattery_text = iotinfo?.power?.battery?.info_text;

	const charging = iotinfo?.power?.charge?.info;
	const ignition = iotinfo?.ignition?.info;
	const temperature = {
		value: iotinfo?.temp_hum?.temperature?.info,
		unit: iotinfo?.temp_hum?.temperature?.unit,
	};
	const humidity = {
		value: iotinfo?.temp_hum?.humidity?.info,
		unit: iotinfo?.temp_hum?.humidity?.unit,
	};
	const altitude = positioninfo?.altitude;
	const stolenmode = device?.stolenmode;
	const dataplan = device?.dataplan;
	const alarmcenter = device?.alarmcenter;
	const photo = FLAVOR === "bike" ? vehicleinfo?.model?.photo : FLAVOR === "alco" ? external_info?.model?.photo : iotinfo?.model?.photo;
	const model = FLAVOR === "bike" ? vehicleinfo?.model?.name : FLAVOR === "alco" ? external_info?.model?.name : iotinfo?.model?.name;
	const bikebattery = vehicleinfo?.currentstatus?.battery?.info;
	const bikecharge = vehicleinfo?.currentstatus?.battery?.charging;
	const visibleswitch = device?.visibleswitch;
	const status_pills = visibleswitch?.status_pills;
	const infoboxes = visibleswitch?.infoboxes;
	const positionExists = iotinfo?.position?.hasposition;
	const deviceOnline = device?.status === "online";
	const engineblock = external_info?.engineblock
	const override = external_info?.override
	
	useEffect(() => {
		if(!currentEvent){
			const content = {
				left: (
						<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={handleClose}>
							<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
						</div>	
					),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t("devices.devices")}
					</motion.h4>
				)
			}
			setTopbarContent(content)
		}
	}, [currentEvent])

	const infoPills = [
		{
			type: "engineblockpending_on",
			visible: status_pills?.enginblockpending_on,
			order: 2,
		},
		{
			type: "engineblock_on",
			visible: status_pills?.engineblock_on,
			order: 2,
		},
		{
			type: "override_on",
			visible: status_pills?.override_on,
			order: 2,
		},
		{
			type: "override_pending",
			visible: status_pills?.override_pending,
			order: 2,
		},
		{
			type: "online",
			visible: status_pills?.online,
			order: 2,
		},
		{
			type: "vehicle_on",
			visible: status_pills?.vehicle_on,
			order: 2,
		},
		{
			type: "sleeping",
			visible: status_pills?.sleeping,
			order: 4,
		},
		{
			type: "vehicle_off",
			visible: status_pills?.vehicle_off,
			order: 4,
		},
		// {
		//     type: "offline",
		//     visible: device?.status !== "online",
		//     order: 4
		// },
		{
			type: "no_dataplan",
			visible: status_pills?.no_dataplan,
			order: 1,
		},
		{
			type: "awaiting_connection",
			visible: status_pills?.awaiting_first_connection,
			order: 5,
		},
		{
			type: "awaiting_position",
			visible: status_pills?.awaiting_first_position,
			order: 5,
		},
		{
			type: "stolenmode",
			visible: status_pills?.stolenmode_on,
			order: 3,
		},
		{
			type: "alarmcenter_armed",
			visible: status_pills?.alarmcenter_armed,
			order: 3,
		},
		{
			type: "alarmcenter_unarmed",
			visible: status_pills?.alarmcenter_unarmed,
			order: 3,
		},
		{
			type: "alarmcenter_triggered",
			visible: status_pills?.alarmcenter_ongoingalarm,
			order: 3,
		},
	];

	const cardList = [
		// {
		// 	name: "assistance_bike",
		// 	valid: infoboxes?.assistancelevel,
		// 	icon: icons.volt,
		// 	label: t("devices.assistance_level"),
		// 	value: vehicleinfo?.currentstatus?.assistancelevel?.info,
		// },
		{
			name: "engineblock",
			valid: infoboxes?.engineblock,
			icon: icons.volt,
			label: t("devices.engine_block"),
			value: engineblock?.pending ? t("devices.pending") : engineblock?.status ? t("devices.on") : t("devices.off"),
			onclick: () => toggleModal("engineblock"),
			spec: true,
		},
		{
			name: "override",
			valid: infoboxes?.override,
			icon: icons.volt,
			label: t("devices.override"),
			value: override?.pending?.active ? t("devices.pending") : override?.current?.active ? t("devices.on") : t("devices.off"),
			onclick: () => toggleModal("override"),
			spec: true,
		},
		{
			name: "battery_info_bike",
			valid: infoboxes?.vehicle_battery,
			icon: icons.volt,
			label: t("devices.battery_info"),
			value: vehicleinfo?.power?.battery?.info,
		},
		{
			name: "battery_charging_bike",
			valid: infoboxes?.vehicle_charge,
			icon: icons.volt,
			label: t("devices.charging_bike"),
			value: vehicleinfo?.power?.charge?.info,
		},
		{
			name: "battery_distance_bike",
			valid: infoboxes?.vehicle_est_remaining_distance,
			icon: icons.volt,
			label: t("devices.distance_bike"),
			value: `${vehicleinfo?.power?.est_remaining_distance?.info} ${vehicleinfo?.power?.est_remaining_distance?.unit}`,
		},
		// {
		// 	name: "lights_bike",
		// 	valid: infoboxes?.lights,
		// 	icon: icons.volt,
		// 	label: t("devices.assistance_level"),
		// 	value: vehicleinfo?.currentstatus?.lights?.info,
		// },
		{
			name: "onoff_bike",
			valid: infoboxes?.vehicle_status,
			icon: icons.volt,
			label: t("devices.on_off_bike"),
			value: vehicleinfo?.status ? t("devices.on") : t("devices.off"),
		},
		// {
		// 	name: "speed_bike",
		// 	valid: infoboxes?.speed,
		// 	icon: icons.volt,
		// 	label: t("devices.on_off_bike"),
		// 	value: `${vehicleinfo?.currentstatus?.speed?.info} ${vehicleinfo?.currentstatus?.speed?.unit}`,
		// },
		{
			name: "external_battery",
			valid: infoboxes?.external_power,
			icon: icons.volt,
			label: t("devices.external_power"),
			value: `${externalbattery ?? "--"} V`,
			//onclick: () => toggleModal("voltage"),
			//spec: true,
		},
		{
			name: "internal_battery",
			valid: infoboxes?.battery,
			icon: charging ? icons.battery.battery_charge : getBatteryIcon(internalbattery_raw),
			label: t("devices.battery"),
			value: charging ? t(`devices.${externalPowerText(t, iotinfo?.model?.id)}`) : `${internalbattery_text ?? "--"}`,

			// value: charging ? t(`devices.${externalPowerText(t, iotinfo?.model?.id)}`) : `${internalbattery ?? "--"}%`,
			//onclick: () => toggleModal("battery"),
			//spec: true,
		},
		{
			name: "distance_today",
			valid: infoboxes?.distancetoday,
			icon: icons.distance,
			label: t("devices.distance_today"),
			value: "HEJ km",
		},
		{
			name: "temperature",
			valid: infoboxes?.temperature,
			icon: icons.temperature,
			label: t("devices.temperature"),
			value: `${(temperature?.value / 100).toFixed(1)}${temperature?.unit}`,
			onclick: () => toggleModal("temp"),
			spec: true,
		},
		{
			name: "humidity",
			valid: infoboxes?.humidity,
			icon: icons.humidity,
			label: t("devices.humidity"),
			value: `${(humidity?.value / 100).toFixed(1)}%`,
			onclick: () => toggleModal("humidity"),
			spec: true,
		},
		{
			name: "altitude",
			valid: infoboxes?.altitude,
			icon: icons.back,
			label: t("devices.altitude"),
			value: altitude ? `${altitude}m ${t("devices.asl")}` : "--",
		},
		{
			name: "ignition",
			valid: infoboxes?.ignition,
			icon: icons.ignition,
			label: t("devices.ignition"),
			value: ignition ? t("devices.on") : t("devices.off"),
		},
		{
			name: "stolen_mode",
			valid: infoboxes?.stolen_mode,
			icon: icons.stolenmode,
			label: t("devices.stolen_mode"),
			value: stolenmode?.active ? t("devices.on") : t("devices.off"),
			onclick: () => toggleModal("stolenmode", stolenmode?.url),
			iconColor: "warning-icon",
			spec: true,
		},
		{
			name: "dataplan",
			valid: infoboxes?.dataplan,
			icon: icons.sidebar.card_active,
			label: t("devices.dataplan"),
			value: dataplan?.dataplan_active ? t("devices.on") : t("devices.off"),
			onclick: () => toggleModal("dataplan"),
			danger: !dataplan?.dataplan_active,
			spec: true,
		},
	];

	const alarmSection = () => {
		const visible = visibleswitch?.alarm;
		const noEvents = events?.length === 0;

		if (!visible) return null;

		// const eventClick = (event) => {

		// 	if(!event?.latitude || !event.longitude) return;
		// 	setActiveDevice(null);
		// 	if (activeEvent === event) {
		// 		setActiveEvent(null);
		// 	} else {
		// 		setActiveEvent(event);
		// 	}
		// };

		// const eventRow = (e, i) => {
		// 	return (
		// 		<div key={i} className={`d-flex bg-light-grey border-${i === 0 ? "top" : "bottom"}-12 p-3 mb-1 align-items-center cursor-pointer`} onClick={() => eventClick(e)}>
		// 			<div className="alarm-img-bg d-flex justify-content-center align-items-center me-3">
		// 				<img className="alarm-img " src={getEventIcon(e.type)} alt={""} />
		// 			</div>
		// 			<p className="m-0 p-0 bold">{e.typedesc}</p>
		// 			<div className="d-flex flex-column ms-auto align-items-end">
		// 				<p className="m-0 p-0 bold">{fixDateEventNew(t, e.date)}</p>
		// 				<p className="m-0 p-0 thin dark_grey">{fixDateEvent(e.date, false)}</p>
		// 			</div>
		// 		</div>
		// 	);
		// };

		return (
			<div className="mb-3">
				<div className="d-flex align-items-center mb-2">
					<img className="primary-icon size_20 me-2" src={icons.sidebar.bell_active} alt="..." />
					<p className="primary bold body me-2">{t("devices.alarms")}</p>
					<div className="cursor-pointer round-bg icon-bg p-1 bg-light-grey m-0 p-0 d-flex align-items-center justify-content-center" onClick={() => goToEvents()}>
						<img className="size_15 dark-grey-icon m-0 p-0" src={icons.chevron_right} alt="..." />
					</div>
				</div>
				{events?.map((event, index) =>(
					<EventRowNew
						key={index}
						event={event}
						devScreen={true}
						index={index}
						currentEvent={currentEvent}
						setCurrentEvent={setCurrentEvent}
					/>
				))}
				{noEvents && 
					<div className="bg-light-grey rounded16 p-3">
						<h4 className="bold">{t("devices.no_alarms")}</h4>
					</div>
				}
			</div>
		);
	};

	const RouteSection = () => {
		const visible = visibleswitch?.location_history;

		if (!visible) return null;
		return <></>;
	};

	const hasMinDate = !!minDate
	const historySection = () => {
		const visible = visibleswitch?.location_history;
		if (!visible) return null;
		return (
			<div className="mb-3">
				<div className="d-flex align-items-center mb-2">
					<img className="primary-icon size_20 me-2" src={icons.sidebar.history_active} alt="..." />
					<p className="primary bold body me-2">{t("devices.location_history")}</p>
				</div>
				<div className="bg-light-grey border-12 p-2">
					<div className="d-flex">
						<TimeSelection selected={dates.preset === 1} className="me-2" onClick={() => changeDatePreset(1)}>
							1 {t("devices.days", { count: 1 })}
						</TimeSelection>
						<TimeSelection selected={dates.preset === 3} className="me-2" onClick={() => changeDatePreset(3)}>
							3 {t("devices.days", { count: 3 })}
						</TimeSelection>
						<TimeSelection selected={dates.preset === 7} className="me-2" onClick={() => changeDatePreset(7)}>
							7 {t("devices.days", { count: 7 })}
						</TimeSelection>
						<TimeSelection selected={dates.preset === 14} onClick={() => changeDatePreset(14)}>
							14 {t("devices.days", { count: 14 })}
						</TimeSelection>
					</div>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
						<div className="d-flex justify-content-start m-0 p-0 mt-3">
							<div className={`d-flex px-2 pt-2 pb-1 bg-white ${wrongDate ? "error-border" : ""} rounded8 flex-column justify-content-center me-2`}>
								<p className="dark_grey bold">{t("devices.start")}</p>
								<DateTimePicker
								id="starttime"
								ampm={false}
								value={dates.startDate}
								onChange={(newValue) => {
									if (!newValue) return;
									if (hasMinDate && newValue.isBefore(minDate)) {
										setWrongDate(true)
										// setDates((d) => ({
										// 	...d,
										// 	startDate: minDate,
										// 	preset: null
										// }))
										return
									}
									setWrongDate(false)
									setDates((d) => ({
									...d,
									startDate: newValue,
									preset: null
									}))
								}}
								minDateTime={hasMinDate ? minDate : undefined}
								maxDateTime={dates.endDate}
								className="date_label align-items-center m-0 p-0 dark_grey"
								format="DD/MM HH:mm"
								slotProps={{
									openPickerButton: { disableRipple: true },
								}}
								/>
							</div>
							<div className="d-flex px-2 py-1 bg-white rounded8 flex-column justify-content-center ms-2">
								<p className="dark_grey bold">{t("devices.end")}</p>
								<DateTimePicker 
								ampm={false} 
								value={dates.endDate} 
								onChange={(newValue) => setDates((d) => ({ ...d, endDate: newValue, preset: null }))} 
								minDateTime={dates.startDate} className="date_label align-items-center m-0 p-0" 
								format="DD/MM HH:mm" 
								slotProps={{
									openPickerButton: {
									disableRipple: true
									}
								}}
								/>
							</div>
							<NewButton 
								notext={true}
								icon={icons.chevron_right}
								iconClass={"white-icon"}
								className={`ms-2 rounded8 bg-grey-blue size_25 align-self-end ${wrongDate ? "disabled" : ""} p-0`} 
								onClick={(event) => getHistory()} />
							{/* <NewButton 
								className="size_25 border-sm cursor-pointer align-self-end bg-grey-blue ms-4"
								notext={true}
								icon={icons.chevron_right}
								iconClass={"white-icon size_15"}
								onClick={() => getHistory()}
							/> */}
							
						</div>
						{wrongDate && <p className="red">{t("devices.date_too_old", {date: fixDate(minDate, true)})}</p>}
					</LocalizationProvider>
				</div>
			</div>
		);
	};

	const CentralSection = () => {
		const visible = visibleswitch?.alarmcentral;

		if (!visible) return null;

		return (
			<div className="mb-3">
				<div className="d-flex align-items-center mb-2">
					<img className="primary-icon size_20 me-2" src={icons.central} alt="..." />
					<p className="primary bold body me-2">{t("devices.alarm_central")}</p>
				</div>
				{alarmcenter?.triggered ? (
					<div className={`p-2 d-flex bg-red border-12 border-light-grey justify-content-center w-100 cursor-pointer`} onClick={() => toggleModal("alarmcentral")}>
						<span className="body bold text-center uppercase text-white">{t("devices.alarm_triggered")}</span>
					</div>
				) : (
					<div className={`p-2 d-flex justify-content-between bg-light-grey border-12 align-items-center`}>
						<span className="body">{t("devices.arm_device")}</span>
						<CustomSwitch 
							checked={alarmcenter?.armed} 
							onChange={() => alarmcenter?.armed ? toggleAlarm(false, false) : toggleModal("alarmcentral")}
							iconChecked={icons.central}
							iconUnchecked={icons.central}
						/>
						{/* <div className="form-check form-switch">
							<input className="form-check-input" type="checkbox" onChange={() => toggleModal("alarmcentral")} role="switch" id="flexSwitchCheckDefault" checked={alarmcenter?.armed} />
						</div> */}
					</div>
				)}
			</div>
		);
	};
	const RelaySection = () => {
		const visible = visibleswitch?.relay_switch;
		const relayinfo = iotinfo?.relayswitch;
		if (!visible) return null;

		return (
			<div className="mb-3">
				<div className="d-flex align-items-center mb-2">
					<img className="primary-icon size_20 me-2" src={icons.relay} alt="..." />
					<p className="primary bold body me-2">{t("devices.relay_switch")}</p>
				</div>
				{relayinfo?.pending ? (
					<div className={`p-2 d-flex bg-grey-blue border-12 justify-content-center w-100 cursor-pointer`} onClick={() => toggleRelay(relayinfo?.info)}>
						<span className="body bold text-center white">{`${relayinfo?.pendingnewmode === true ? t("devices.on") : t("devices.off")} ${t("devices.pending_click")}`}</span>
					</div>
				) : (
					<div className={`p-2 d-flex justify-content-between bg-light-grey border-12 align-items-center`}>
						<span className="body">{t("devices.toggle_relay")}</span>
						<CustomSwitch 
							checked={relayinfo?.info} 
							onChange={() => toggleRelay(!relayinfo?.info)}
							iconChecked={icons.relay}
							iconUnchecked={icons.relay}
						/>
					</div>
				)}
			</div>
		);
	};

	const changeDatePreset = (time) => {
		setDates({
			startDate: dayjs().subtract(time, "day"),
			endDate: dayjs(),
			preset: time,
		});
	};

	const updateAgo = () => {
		if (!device) return;
		const now = new Date();
		const lastUpdate = new Date(device?.lastupdate);
		const diffMs = now.getTime() - lastUpdate.getTime();
		const diffMinutes = Math.floor(diffMs / (1000 * 60));
		const diffHours = Math.floor(diffMinutes / 60);
		const diffDays = Math.floor(diffHours / 24);
		const diffMonths = Math.floor(diffDays / 30);
		const diffYears = Math.floor(diffDays / 365);
		if(!device?.lastupdate){
			return t("devices.never_connected")
		} else if (diffYears > 0) {
			return `${diffYears} ${t("devices.years_ago", { count: diffYears })}`;
		} else if (diffMonths > 0) {
			return `${diffMonths} ${t("devices.months_ago", { count: diffMonths })}`;
		} else if (diffDays > 0) {
			return `${diffDays} ${t("devices.days_ago", { count: diffDays })}`;
		} else if (diffHours > 0) {
			return `${diffHours} ${t("devices.hours_ago", { count: diffHours })}`;
		} else {
			return `${diffMinutes} ${t("devices.minutes_ago", { count: diffMinutes })}`;
		}
	};

	return (
		<div className="position-relative" style={{ minHeight: 600 }}>
			<div className="d-flex flex-column position-relative">
				<img src={photo} className="w-100 modelphoto" alt="..." />
				<div className="px-3">
					<div className="d-flex w-100">
						<img className="size24 align-self-center primary-icon" src={charging || bikecharge ? icons.battery.battery_charge : getBatteryIcon(bikebattery ? bikebattery : internalbattery_raw)} alt="" />
						<div className="d-flex flex-column ms-3 overflow-hidden">
							<p className="body bold standard text-truncate">{device?.name}</p>
							<p className="">{model}</p>
						</div>
						{positionExists &&
							<NewButton 
								notext={true} 
								icon={icons.location_arrow} 
								iconClass={`${deviceOnline ? "white-white-icon" : "drak-grey-icon"}`} 
								className={`${deviceOnline ? "bg-gradient" : "bg-grey" } ms-auto full-rounded size_36`} 
								onClick={(event) => showOnMap(event, device)} />
						}
					</div>
					<div className="d-flex mt-2">
						{infoPills
							.filter((pill) => pill.visible)
							.sort((a, b) => a.order - b.order)
							.map((pill, index) => (
								<InfoPill key={index} type={pill.type} />
							))}
					</div>
				</div>
				<div className="bg-white mt-2 px-3 py-3">
					<div className="d-flex align-items-center mb-2">
						<img className="primary-icon size_20 me-2" src={icons.info_icon} alt="..." />
						<p className="primary bold body me-2">{t("devices.info")}</p>
						<p className="primary">{`${t("devices.updated")}: ${updateAgo()}`}</p>
					</div>
					<div className="d-flex flex-wrap">
						{cardList.map(
							(card, index) =>
								card.valid && (
									<motion.div key={index} className="device-card px-2 mb-3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: index * 0.03 }}>
										<DeviceInfoCardWIP iconColor={card.iconColor} danger={card.danger} spec={card.spec} onClick={card.onclick} name={card.name} index={index} value={card.value} label={card.label} icon={card.icon} />
									</motion.div>
								)
						)}
					</div>
					<RouteSection />
					{historySection()}
					<CentralSection />
					<RelaySection />
					{alarmSection()}
				</div>
			</div>
			<AlcolockModal show={(modals[alcoType] ?? false)} type={alcoType} data={external_info} onClose={() => toggleModal(alcoType)} save={(data) => updateAlcoInfo(data)} />
			<ExtendedInfoModal show={modals[extendedType] ?? false} type={extendedType} data={extendedData} onClose={() => toggleModal(extendedType)} turnOffStolenMode={() => turnOffStolenMode()} />
			<ZoneModal show={modals["alarmcentral"] ?? false} onClose={() => toggleModal("alarmcentral")} activateAlarm={() => toggleAlarm(true)} deactivateAlarm={() => toggleAlarm(false)} device={device} />
		</div>
	);
};

export default SelectedDevice;
